import { useEffect, useState } from "react";
import { ArrowLeft, BookOpen, Download, FileText, Loader2, Sparkles, UserCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

/**
 * Personalisiertes Handbuch generieren – nur für Admin + Vertriebsdirektor.
 * Empfängername + Rolle → PDF-Download.
 */
export default function AdminHandbook() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("sales_rep");
  const [busy, setBusy] = useState(false);
  const [quizBusy, setQuizBusy] = useState(null); // "full" | "questions" | null

  // Guard: only admin + sales_director may see this page.
  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "sales_director") {
      toast.error("Dieser Bereich ist nur für Admin & Vertriebsdirektor zugänglich.");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Load available role variants.
  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "sales_director")) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/handbook/roles");
        setRoles(data.roles || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Rollen konnten nicht geladen werden");
      }
    })();
  }, [user]);

  const generate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Bitte den Empfängernamen eingeben.");
      return;
    }
    setBusy(true);
    try {
      const resp = await api.post(
        "/admin/handbook/generate",
        { recipient_name: trimmed, recipient_role: role },
        { responseType: "blob", timeout: 60_000 }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const safe = trimmed.replace(/[^a-zA-Z0-9 _-]/g, "").replace(/ /g, "_") || "Mitarbeiter";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Novatus_Handbuch_${safe}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Handbuch heruntergeladen");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Generierung fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Generierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const currentRoleLabel = roles.find((r) => r.id === role)?.label || role;

  const downloadQuiz = async (withSolutions) => {
    const variant = withSolutions ? "full" : "questions";
    setQuizBusy(variant);
    try {
      const resp = await api.get("/admin/handbook/quiz", {
        params: { with_solutions: withSolutions },
        responseType: "blob",
        timeout: 60_000,
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = withSolutions
        ? "Novatus_Schulungsquiz_mit_Loesungen.pdf"
        : "Novatus_Schulungsquiz_Fragebogen.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(
        withSolutions
          ? "Quiz inkl. Lösungen heruntergeladen"
          : "Quiz-Fragebogen heruntergeladen"
      );
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Generierung fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Generierung fehlgeschlagen");
    } finally {
      setQuizBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 px-4 py-5 max-w-3xl mx-auto" data-testid="admin-handbook-page">
      <header className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="h-10 w-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
          data-testid="handbook-back-btn"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#00E575]/80 font-bold">
            Direktorat
          </div>
          <h1 className="font-display text-2xl font-bold leading-tight">Handbuch generieren</h1>
        </div>
      </header>

      <section className="bg-[#0F1014] border border-[#00E575]/25 rounded-2xl p-5 mb-5">
        <div className="flex gap-3 items-start">
          <div className="h-11 w-11 rounded-xl bg-[#00E575]/15 border border-[#00E575]/30 flex items-center justify-center flex-shrink-0">
            <BookOpen size={20} className="text-[#00E575]" />
          </div>
          <div className="text-[13px] text-zinc-300 leading-relaxed">
            Erstellt ein PDF-Handbuch, das auf dem Deckblatt den Empfängernamen
            trägt und im Inhalt zur ausgewählten Rolle passt. Vertriebsleiter
            erhalten zusätzlich Kanban &amp; Team-Analytics, Direktoren das
            Pipeline-Health-Dashboard.
          </div>
        </div>
      </section>

      <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4">
        <div>
          <label className="block text-[11.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5">
            Empfängername
          </label>
          <div className="relative">
            <UserCircle2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="handbook-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 120))}
              placeholder="z. B. Anna Schneider"
              disabled={busy}
              className="w-full h-11 bg-[#09090B] border border-zinc-700 rounded-xl pl-9 pr-3 text-[14px] text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
            />
          </div>
          <div className="text-[10.5px] text-zinc-500 mt-1">
            Dieser Name erscheint groß auf dem Deckblatt sowie in der Begrüßung.
          </div>
        </div>

        <div>
          <label className="block text-[11.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5">
            Rolle / Variante
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {roles.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={`handbook-role-${r.id}`}
                onClick={() => setRole(r.id)}
                disabled={busy}
                className={`h-12 rounded-xl border text-left px-4 transition active:scale-[0.99] ${
                  role === r.id
                    ? "bg-[#00E575]/10 border-[#00E575] text-zinc-100"
                    : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500"
                }`}
              >
                <div className="text-[13px] font-semibold">{r.label}</div>
                <div className="text-[10.5px] text-zinc-500 font-mono">{r.id}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="pt-2 flex flex-col sm:flex-row gap-2">
          <button
            onClick={generate}
            disabled={busy || !name.trim()}
            data-testid="handbook-generate-btn"
            className="flex-1 h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00C964] disabled:opacity-50 active:scale-[0.98] transition"
          >
            {busy ? (
              <><Loader2 className="animate-spin" size={18} /> Generiere…</>
            ) : (
              <><Download size={18} /> Handbuch als PDF herunterladen</>
            )}
          </button>
        </div>
      </section>

      <section className="mt-6 bg-[#0F1014] border border-zinc-800 rounded-2xl p-4 flex items-start gap-3">
        <Sparkles size={16} className="text-[#00E575] mt-0.5 flex-shrink-0" />
        <div className="text-[12.5px] text-zinc-400 leading-relaxed">
          <b className="text-zinc-200">Vorschau:</b> Die PDF-Variante „{currentRoleLabel}"
          enthält {role === "sales_rep" && "Tagesgeschäft (Dashboard, Projektliste, Tech-Check, Medien, Angebot)."}
          {role === "sales_manager" && "Tagesgeschäft plus Kanban-Board und Team-Analytics."}
          {role === "sales_director" && "alle Vertriebs-Inhalte plus Pipeline-Health-Dashboard und Handbuch-Versand."}
          {role === "admin" && "den vollen Umfang inkl. Administration und API-Konfiguration."}
          {" "}Zusätzlich enthält jedes Handbuch <b className="text-zinc-200">Teil VIII „Schulung – WP-Fachwissen"</b> (15 Kapitel).
        </div>
      </section>

      <section className="mt-6 bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="quiz-section">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-[#00E575]/15 border border-[#00E575]/30 flex items-center justify-center flex-shrink-0">
            <FileText size={20} className="text-[#00E575]" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold leading-tight">Schulungsquiz (Tag 2)</h2>
            <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-1">
              30 Multiple-Choice-Fragen aus den 15 Schulungs-Kapiteln. Zwei Varianten:
              komplette Datei mit Lösungen für den Schulungsleiter, oder nur der Fragebogen
              als Handout für die Teilnehmer.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            onClick={() => downloadQuiz(true)}
            disabled={quizBusy !== null}
            data-testid="quiz-with-solutions-btn"
            className="h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00C964] disabled:opacity-50 active:scale-[0.98] transition"
          >
            {quizBusy === "full" ? (
              <><Loader2 className="animate-spin" size={18} /> Generiere…</>
            ) : (
              <><Download size={18} /> Quiz inkl. Lösungen</>
            )}
          </button>
          <button
            onClick={() => downloadQuiz(false)}
            disabled={quizBusy !== null}
            data-testid="quiz-questions-only-btn"
            className="h-12 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-100 font-bold inline-flex items-center justify-center gap-2 hover:border-[#00E575]/60 disabled:opacity-50 active:scale-[0.98] transition"
          >
            {quizBusy === "questions" ? (
              <><Loader2 className="animate-spin" size={18} /> Generiere…</>
            ) : (
              <><Download size={18} /> Nur Fragebogen (Handout)</>
            )}
          </button>
        </div>
      </section>
    </div>
  );
}
